'use client'
import { hubHandler } from '@core/auth/utils'
import { AuthHandler } from '@core/auth/utils/auth-handler'
import { useCallback, useEffect, useState } from 'react'

import { useGetHubsList } from '../use-get-hubs-list/use-get-hubs-list.hook'
import useProfile from '../use-profile/use-profile.hook'

interface LoadingStatus {
  progress: number
  isLoaded: boolean
}

const ITEM_WEIGHTS = {
  token: 15,
  refreshToken: 10,
  user: 20,
  permissions: 15,
  hubList: 20,
  hubSelected: 20,
}

export function useAppLoading(): LoadingStatus {
  const { accessToken, refreshToken } = AuthHandler.getTokens()
  const { fetchUserProfile, user: userProfile, userPermission } = useProfile()
  const { getHubsList, getHubIdSelected } = hubHandler()
  const { fetchHubs } = useGetHubsList()
  const hubsList = getHubsList()
  const hubIdSelected = getHubIdSelected()

  const [hasToken, setHasToken] = useState<boolean>(!!accessToken)
  const [hasRefreshToken, setHasRefreshToken] =
    useState<boolean>(!!refreshToken)
  const [hasUser, setHasUser] = useState<boolean>(!!userProfile?.name)
  const [hasPermissions, setHasPermissions] = useState<boolean>(
    !!userPermission?.length
  )
  const [hasHubList, setHasHubList] = useState<boolean>(!!hubsList?.length)
  const [hasHubSelected, setHasHubSelected] = useState<boolean>(!!hubIdSelected)

  const [progress, setProgress] = useState<number>(5)

  const calculateProgress = useCallback(() => {
    let currentProgress = 0

    if (hasToken) currentProgress += ITEM_WEIGHTS.token
    if (hasRefreshToken) currentProgress += ITEM_WEIGHTS.refreshToken
    if (hasUser) currentProgress += ITEM_WEIGHTS.user
    if (hasPermissions) currentProgress += ITEM_WEIGHTS.permissions
    if (hasHubList) currentProgress += ITEM_WEIGHTS.hubList
    if (hasHubSelected) currentProgress += ITEM_WEIGHTS.hubSelected

    setProgress(currentProgress)
  }, [
    hasToken,
    hasRefreshToken,
    hasUser,
    hasPermissions,
    hasHubList,
    hasHubSelected,
  ])

  const forceLoadMissingItems = useCallback(() => {
    if (!hasToken || !hasRefreshToken) {
      setHasToken(!!accessToken)
      setHasRefreshToken(!!refreshToken)
    }

    if ((!hasUser || !hasPermissions) && userProfile) {
      setHasUser(!!userProfile.name)
      setHasPermissions(!!userPermission?.length)
    }

    if (!userProfile) {
      fetchUserProfile()
    }

    if (!hasHubList) {
      if (hubsList?.length) {
        setHasHubList(!!hubsList.length)
      } else {
        fetchHubs()
      }
    }

    if (!hasHubSelected) {
      setHasHubSelected(!!hubIdSelected)
    }
  }, [
    hasToken,
    hasRefreshToken,
    hasUser,
    hasPermissions,
    userProfile,
    hasHubList,
    hasHubSelected,
    accessToken,
    refreshToken,
    userPermission?.length,
    fetchUserProfile,
    hubsList?.length,
    fetchHubs,
    hubIdSelected,
  ])

  useEffect(() => {
    calculateProgress()

    if (progress < 100) {
      const interval = setInterval(() => {
        forceLoadMissingItems()
      }, 300)

      return () => clearInterval(interval)
    }
  }, [progress, calculateProgress, forceLoadMissingItems])

  return {
    progress,
    isLoaded: progress === 100,
  }
}
